import { useState } from "react";
import { Button, Field, cx, inputBase } from "@/platform/ui";
import { IconX } from "@/platform/ui";
import { REMINDER_PRESETS, REMINDER_UNITS, reminderLabel } from "@/planning/ui/lib/reminders.js";

// EventModal's "Reminders" field: the chip list of already-added reminders,
// a preset dropdown, and a custom amount+unit entry. Owns its own
// in-progress custom-reminder input state — that draft never needs to
// survive outside this widget.
export function ReminderPicker({
  reminders,
  onChange,
}: {
  reminders: number[];
  onChange: (reminders: number[]) => void;
}) {
  const [customAmt, setCustomAmt] = useState("");
  const [customUnit, setCustomUnit] = useState<(typeof REMINDER_UNITS)[number]["value"]>("min");

  const addReminder = (m: number) =>
    onChange([...new Set([...reminders, m])].sort((a, b) => a - b));
  const removeReminder = (m: number) => onChange(reminders.filter((x) => x !== m));
  const addCustomReminder = () => {
    const amt = parseInt(customAmt, 10);
    const unit = REMINDER_UNITS.find((u) => u.value === customUnit)!;
    if (!Number.isFinite(amt) || amt <= 0) return;
    addReminder(amt * unit.minutes);
    setCustomAmt("");
  };

  return (
    <Field label="Reminders">
      <div className="space-y-2">
        {reminders.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {reminders.map((m) => (
              <span
                key={m}
                className="flex items-center gap-1 rounded-full border border-line bg-surface-2 px-2 py-0.5 text-[11px] font-bold text-dim"
              >
                {reminderLabel(m)} before
                <button
                  onClick={() => removeReminder(m)}
                  className="text-faint hover:text-negative"
                  aria-label="Remove reminder"
                >
                  <IconX size={11} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={cx(inputBase, "w-36")}
            value=""
            onChange={(e) => e.target.value && addReminder(Number(e.target.value))}
          >
            <option value="">Add reminder…</option>
            {REMINDER_PRESETS.filter((p) => !reminders.includes(p.minutes)).map((p) => (
              <option key={p.minutes} value={p.minutes}>
                {p.label}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-faint">or</span>
          <input
            type="number"
            min={1}
            className={cx("mono", inputBase, "w-16")}
            value={customAmt}
            onChange={(e) => setCustomAmt(e.target.value)}
            placeholder="10"
          />
          <select
            className={cx(inputBase, "w-24")}
            value={customUnit}
            onChange={(e) => setCustomUnit(e.target.value as typeof customUnit)}
          >
            {REMINDER_UNITS.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
          <Button variant="ghost" onClick={addCustomReminder} disabled={!customAmt}>
            Add
          </Button>
        </div>
      </div>
    </Field>
  );
}
